import * as Icons from '@ant-design/icons';
import { Button, Card, Dropdown, Form, Space, Typography } from 'antd';
import { DocumentItemField } from '../../../../components/documentation/DocumentField';
import { DocumentItemImage } from './DocumentItemImage';
import { IDocument } from 'interfaces/documentation/document';
import { ModalUploadDocument } from './ModalUploadDocument';
import { actionableErrorMessage } from 'utils/actionable-error-message';
import { formatDate } from 'services/date';
import { getApproveDocumentsPayload } from 'services/documents';
import { useCallback, useState } from 'react';
import { useDocumentationStore } from '../documentation-store';
import { useParams } from 'react-router-dom';
import { useUpdate } from '@refinedev/core';
import type { MenuProps } from 'antd';

const { Text } = Typography;

interface DocumentItemProps {
  document: IDocument;
}

export const DocumentItem = ({ document }: DocumentItemProps) => {
  const [form] = Form.useForm();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const { id } = useParams();
  const { mutateAsync: updateDocument } = useUpdate();
  const { created_at, attribute_fields, feedback } = document;
  const openModal = useDocumentationStore(s => s.openModal);
  const queryHandler = useDocumentationStore(s => s.queryHandler);

  const submit = useCallback(
    (values: Record<string, unknown>) => {
      openModal('approve', getApproveDocumentsPayload(document, values));
    },
    [document, openModal]
  );

  const handleEdit = useCallback(
    (values: Record<string, unknown>) => {
      const updatedDocument = getApproveDocumentsPayload(document, values);

      const payload = {
        attribute_fields: updatedDocument.attribute_fields,
        documentation_id: id,
        id: document.id,
        document: {
          attribute_fields: updatedDocument.attribute_fields
        }
      };

      updateDocument({
        resource: `documentations/${id}/documents/`,
        id: document.id,
        values: payload,
        successNotification: () => ({
          type: 'success',
          description: 'Documento editado com sucesso',
          message: 'As alterações foram salvas'
        }),
        errorNotification: error => ({
          type: 'error',
          description: 'Erro ao editar documento',
          message: actionableErrorMessage(
            error,
            'Não foi possível salvar as alterações. Tente novamente.'
          )
        })
      })
        .then(() => {
          queryHandler();
        })
        .catch(() => null);
    },
    [document, id, queryHandler, updateDocument]
  );

  const handleNewUpload = useCallback(() => {
    setUploadModalOpen(true);
  }, []);

  const handleChangeCategory = useCallback(() => {
    openModal('setKind', document);
  }, [document, openModal]);

  const editMenuItems: MenuProps['items'] = [
    {
      key: 'new-upload',
      label: 'Novo upload do arquivo',
      icon: <Icons.UploadOutlined />,
      onClick: handleNewUpload
    },
    {
      key: 'change-category',
      label: 'Trocar categoria do documento',
      icon: <Icons.HistoryOutlined />,
      onClick: handleChangeCategory
    }
  ];

  const isApproved = document.status === 'approved';
  const isRejected = document.status === 'rejected';

  return (
    <Form form={form} layout="vertical" onFinish={submit}>
      <Space direction="vertical" size="middle" className="w-full">
        <Space size={['middle', undefined]} wrap>
          {attribute_fields?.map((field, index) => (
            <DocumentItemField key={index} field={field} />
          ))}
        </Space>

        <div>
          <DocumentItemImage document={document} />
          <Text className="mt-2">
            Documento enviado em {formatDate(created_at, 'DD/MM/YYYY - HH:mm')}
          </Text>
        </div>

        {feedback && (
          <Card
            type="inner"
            size={'small'}
            title={'Por que este documento foi indeferido?'}
          >
            <Typography.Text>{feedback}</Typography.Text>
          </Card>
        )}

        <Space className="w-full justify-between pt-2 bg-neutral-100 rounded-md p-2">
          <Space>
            {attribute_fields && attribute_fields.length > 0 && (
              <Button
                icon={<Icons.SyncOutlined />}
                onClick={() => {
                  form
                    .validateFields()
                    .then(values => {
                      handleEdit(values);
                    })
                    .catch(() => null);
                }}
              >
                Atualizar
              </Button>
            )}
            <Dropdown menu={{ items: editMenuItems }} placement="bottomLeft">
              <Button icon={<Icons.EditOutlined />}>
                <Space>
                  Editar documento
                  <Icons.DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </Space>

          <Space>
            <Button
              variant={isRejected ? 'solid' : 'outlined'}
              color={isRejected ? 'danger' : 'default'}
              icon={<Icons.CloseCircleOutlined />}
              onClick={
                isRejected ? undefined : () => openModal('reject', document)
              }
              aria-disabled={isRejected}
              tabIndex={isRejected ? -1 : 0}
            >
              {isRejected ? 'Rejeitado' : 'Rejeitar'}
            </Button>
            <Button
              variant={isApproved ? 'solid' : 'outlined'}
              color={isApproved ? 'green' : 'default'}
              icon={<Icons.CheckCircleOutlined />}
              onClick={
                isApproved
                  ? undefined
                  : () => {
                      form
                        .validateFields()
                        .then(values => {
                          openModal(
                            'approve',
                            getApproveDocumentsPayload(document, values)
                          );
                        })
                        .catch(() => null);
                    }
              }
              aria-disabled={isApproved}
              tabIndex={isApproved ? -1 : 0}
            >
              {isApproved ? 'Aprovado' : 'Aprovar'}
            </Button>
          </Space>
        </Space>
      </Space>

      <ModalUploadDocument
        modalProps={{
          open: uploadModalOpen,
          onCancel: () => setUploadModalOpen(false)
        }}
        modalClose={() => setUploadModalOpen(false)}
        document={document}
        refetch={queryHandler}
      />
    </Form>
  );
};
